import { useState } from "react";
import { Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useFounder } from "@/lib/founder/useFounder";
import { useFounderKeys } from "@/lib/founder/useFounderKeys";
import { toast } from "@/hooks/use-toast";
import UserManagementPanel from "@/components/founder/UserManagementPanel";
import QuickActionsPanel from "@/components/founder/QuickActionsPanel";
import RoleManagementPanel from "@/components/founder/RoleManagementPanel";
import AnalyticsPanel from "@/components/founder/AnalyticsPanel";
import AuditLogPanel from "@/components/founder/AuditLogPanel";
import { BroadcastComposerPanel } from "@/components/founder/BroadcastComposerPanel";
import ModQueuePanel from "@/components/founder/ModQueuePanel";
import MonitoringControlsPanel from "@/components/founder/MonitoringControlsPanel";
import ReviewQueuePanel from "@/components/founder/ReviewQueuePanel";
import SafeVenuesAdminPanel from "@/components/founder/SafeVenuesAdminPanel";
import ContributionsPanel from "@/components/founder/ContributionsPanel";
import SmsOutboxPanel from "@/components/founder/SmsOutboxPanel";
import InfluencerCodesPanel from "@/components/founder/InfluencerCodesPanel";
import InfluencerCodeStatusWidget from "@/components/founder/InfluencerCodeStatusWidget";
import InfluencerRedemptionsPanel from "@/components/founder/InfluencerRedemptionsPanel";
import DemoAccountsPanel from "@/components/founder/DemoAccountsPanel";
import StoreVendorApprovalPanel from "@/components/founder/StoreVendorApprovalPanel";
import { RefreshSessionButton } from "@/components/RefreshSessionButton";
import { GrantAdminButton } from "@/components/founder/GrantAdminButton";
import { FounderLinkStatus } from "@/components/founder/FounderLinkStatus";

const KeysPanel = () => {
  const { keys, loading, mint, revoke, mintedToken, clearMintedToken } = useFounderKeys();
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [isMaster, setIsMaster] = useState(false);

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    if (isMaster && !confirm(
      "Mint a RAELA MASTER key? This grants RAELA full ecosystem-wide access to every app " +
      "(read/write/delete on all non-founder tables). Only revocable by you."
    )) return;
    setBusy(true);
    const result = await mint(label.trim(), isMaster);
    setBusy(false);
    if (result) {
      setLabel("");
      setIsMaster(false);
      toast({
        title: isMaster ? "Master key minted" : "Key minted",
        description: "Copy it now — it won't be shown again.",
      });
    } else {
      toast({ title: "Mint failed", description: "Try again.", variant: "destructive" });
    }
  };

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({ title: "Copy failed — select and copy manually" });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <div>
          <h3 className="text-base font-medium">Generate RAELA / app admin token</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Tokens grant full access to RAELA and every founder-aware edge function. Treat them
            like passwords. They are shown <span className="text-foreground">once</span>; we
            store only a hash.
          </p>
        </div>
        <form onSubmit={handleMint} className="space-y-3">
          <div className="flex gap-3">
            <Input
              placeholder="Label (e.g. 'RAELA local dev', 'Postman', 'CI')"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={80}
              required
            />
            <Button type="submit" disabled={busy || !label.trim()}>
              {busy ? "Minting…" : isMaster ? "Mint Master" : "Generate"}
            </Button>
          </div>
          <label className="flex items-start gap-3 rounded-md border border-dashed border-border p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isMaster}
              onChange={(e) => setIsMaster(e.target.checked)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">RAELA Master API key (ecosystem-wide)</div>
              <p className="text-xs text-muted-foreground mt-1">
                Adds the <span className="font-mono">raela_master</span> scope. Lets RAELA call
                <span className="font-mono"> raela-master-action</span> to autonomously read,
                insert, update, or delete data across every app and table — except
                <span className="font-mono"> founders</span>,
                <span className="font-mono"> founder_admin_keys</span>,
                <span className="font-mono"> user_roles</span>, and any founder profile.
                Revocable only by you.
              </p>
            </div>
          </label>
        </form>

        {mintedToken && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
            <div className="text-sm font-medium text-amber-600 dark:text-amber-400">
              Copy this token now — you won't see it again.
            </div>
            <div className="font-mono text-xs break-all bg-background/60 p-3 rounded border border-border">
              {mintedToken.token}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => copyToken(mintedToken.token)}>Copy token</Button>
              <Button size="sm" variant="outline" onClick={clearMintedToken}>I've saved it</Button>
            </div>
          </div>
        )}
      </Card>

      <section className="space-y-3">
        <h3 className="text-base font-medium">Active tokens</h3>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tokens yet.</p>
        ) : (
          <ul className="space-y-2">
            {keys.map((k) => (
              <li key={k.id}>
                <Card className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{k.label}</div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">
                      {k.token_prefix}…
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {k.scopes.join(" · ")} · created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  {k.revoked_at ? (
                    <Badge variant="outline" className="text-muted-foreground">Revoked</Badge>
                  ) : (
                    <Button
                      size="sm" variant="ghost"
                      onClick={async () => {
                        if (!confirm(`Revoke "${k.label}"?`)) return;
                        await revoke(k.id);
                        toast({ title: "Revoked" });
                      }}
                    >
                      Revoke
                    </Button>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const Founder = () => {
  const { session, loading: authLoading } = useAuth();
  const { isFounder, loading: founderLoading } = useFounder();

  if (authLoading || founderLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="mx-auto max-w-5xl px-6 py-16 text-muted-foreground">Loading…</main>
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace />;
  if (!isFounder) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-6 py-12 space-y-8">
        <header className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Badge variant="secondary">Founder</Badge>
              <h1 className="text-3xl font-semibold tracking-tight">Founder portal</h1>
              <p className="text-muted-foreground">
                Permanent <span className="text-foreground">Attuned</span> access. Full app admin
                across every Wavlynq community. Every mutation here is server-audited.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <GrantAdminButton />
              <RefreshSessionButton />
            </div>
          </div>
        </header>

        <FounderLinkStatus />

        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="roles">Roles</TabsTrigger>
            <TabsTrigger value="modqueue">Mod queue</TabsTrigger>
            <TabsTrigger value="reviewqueue">Review queue</TabsTrigger>
            <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
            <TabsTrigger value="venues">Safe venues</TabsTrigger>
            <TabsTrigger value="broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="analytics">Analytics & Export</TabsTrigger>
            <TabsTrigger value="audit">Audit log</TabsTrigger>
            <TabsTrigger value="keys">RAELA tokens</TabsTrigger>
            <TabsTrigger value="contributions">Contributions</TabsTrigger>
            <TabsTrigger value="sms">SMS outbox</TabsTrigger>
            <TabsTrigger value="influencer-codes">Influencer codes</TabsTrigger>
            <TabsTrigger value="influencers-only">Influencers only</TabsTrigger>
            <TabsTrigger value="influencer-redemptions">Code redemptions</TabsTrigger>
            <TabsTrigger value="demo-accounts">Demo accounts</TabsTrigger>
            <TabsTrigger value="store-vendors">Store vendors</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="space-y-6">
              <QuickActionsPanel />
              <UserManagementPanel />
            </div>
          </TabsContent>
          <TabsContent value="roles"><RoleManagementPanel /></TabsContent>
          <TabsContent value="modqueue"><ModQueuePanel /></TabsContent>
          <TabsContent value="reviewqueue"><ReviewQueuePanel /></TabsContent>
          <TabsContent value="monitoring"><MonitoringControlsPanel /></TabsContent>
          <TabsContent value="venues"><SafeVenuesAdminPanel /></TabsContent>
          <TabsContent value="broadcast"><BroadcastComposerPanel /></TabsContent>
          <TabsContent value="analytics"><AnalyticsPanel /></TabsContent>
          <TabsContent value="audit"><AuditLogPanel /></TabsContent>
          <TabsContent value="keys"><KeysPanel /></TabsContent>
          <TabsContent value="contributions"><ContributionsPanel /></TabsContent>
          <TabsContent value="sms"><SmsOutboxPanel /></TabsContent>
          <TabsContent value="influencer-codes">
            <div className="space-y-6">
              <InfluencerCodeStatusWidget />
              <InfluencerCodesPanel />
            </div>
          </TabsContent>
          <TabsContent value="influencer-redemptions"><InfluencerRedemptionsPanel /></TabsContent>
          <TabsContent value="influencers-only">
            <UserManagementPanel initialInfluencerOnly />
          </TabsContent>
          <TabsContent value="demo-accounts"><DemoAccountsPanel /></TabsContent>
          <TabsContent value="store-vendors"><StoreVendorApprovalPanel /></TabsContent>
        </Tabs>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Founder;
